import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Star, 
  MagnifyingGlass,
  Funnel,
  Phone,
  Globe,
  Tag
} from '@phosphor-icons/react';

const FALLBACK = 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800';
const resolveImg = (src) => {
  if (!src) return FALLBACK;
  return src;
};
import { getWineries, getWineryCount } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const Wineries = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [wineries, setWineries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [hasDeals, setHasDeals] = useState(searchParams.get('deals') === 'true');

  const regions = ['Napa Valley', 'Sonoma', 'Carneros', 'St. Helena', 'Calistoga', 'Yountville', 'Oakville'];

  useEffect(() => {
    const fetchWineries = async () => {
      setLoading(true);
      try {
        const params = {
          limit: 50,
          ...(search && { search }),
          ...(region && { region }),
          ...(hasDeals && { has_deal: true })
        };
        
        const [wineriesRes, countRes] = await Promise.all([
          getWineries(params),
          getWineryCount()
        ]);
        
        setWineries(wineriesRes.data);
        setTotalCount(countRes.data.count);
      } catch (error) {
        console.error('Error fetching wineries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWineries();
  }, [search, region, hasDeals]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (search) newParams.set('search', search);
    if (region) newParams.set('region', region);
    if (hasDeals) newParams.set('deals', 'true');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearch('');
    setRegion('');
    setHasDeals(false);
    setSearchParams({});
  };

  return (
    <>
      <SEO
        title="500+ Wineries in Napa Valley & Sonoma"
        description="Browse our complete directory of 500+ wineries in Napa Valley and Sonoma. Find wineries with deals, reviews, and plan your wine tasting adventure."
        path="/wineries"
        image="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Wineries', path: '/wineries' }
        ]}
      />

      {/* Hero */}
      <section 
        className="pt-24 sm:pt-32 pb-10 sm:pb-16 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(93, 16, 29, 0.9), rgba(93, 16, 29, 0.95)), url('https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1920&q=80')`
        }}
        data-testid="wineries-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="overline text-gold-400 mb-3 sm:mb-4 block text-xs sm:text-xs">Wine Directory</span>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
              Explore <span className="italic">{totalCount}+ Wineries</span>
            </h1>
            <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto px-4">
              Discover the best wineries in Napa Valley, Sonoma, and California Wine Country.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-5 sm:py-8 bg-muted/50 border-b border-border" data-testid="wineries-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground sm:w-5 sm:h-5" />
                <Input
                  type="text"
                  placeholder="Search wineries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-10 sm:h-11 text-sm"
                  data-testid="winery-search-input"
                />
              </div>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="h-10 sm:h-11 px-3 sm:px-4 rounded-md border border-input bg-background text-sm"
                data-testid="region-select"
              >
                <option value="">All Regions</option>
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasDeals}
                  onChange={(e) => setHasDeals(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  data-testid="deals-checkbox"
                />
                <span className="text-xs sm:text-sm">With Deals Only</span>
              </label>
              <div className="flex gap-2 ml-auto">
                <Button type="submit" className="h-9 sm:h-10 text-xs sm:text-sm px-3 sm:px-4" data-testid="search-btn">
                  <Funnel size={16} className="mr-1.5 sm:mr-2 sm:w-[18px] sm:h-[18px]" />
                  Filter
                </Button>
                {(search || region || hasDeals) && (
                  <Button type="button" variant="outline" onClick={clearFilters} className="h-9 sm:h-10 text-xs sm:text-sm px-3 sm:px-4" data-testid="clear-filters-btn">
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </section>


      {/* Winery Grid */}
      <section className="py-8 sm:py-12" data-testid="wineries-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-muted animate-pulse rounded-lg h-60 sm:h-80" />
              ))}
            </div>
          ) : wineries.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <p className="text-muted-foreground text-sm sm:text-base">No wineries found matching your criteria.</p>
              <Button onClick={clearFilters} className="mt-4 h-9 sm:h-10 text-sm">Clear Filters</Button>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground text-sm mb-4 sm:mb-6">
                Showing {wineries.length} of {totalCount} wineries
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {wineries.map((winery, index) => (
                  <React.Fragment key={`winery-frag-${winery.id}`}>
                  <motion.div
                    key={winery.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.05, 0.3) }}
                  >
                    <Link 
                      to={`/wineries/${winery.id}`}
                      className="winery-card block rounded-lg overflow-hidden h-full"
                      data-testid={`winery-card-${winery.id}`}
                    >
                      <div className="aspect-video sm:aspect-[4/3] overflow-hidden relative bg-muted">
                        <img
                          src={resolveImg(winery.image_url)}
                          alt={winery.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="eager"
                          referrerPolicy="no-referrer"
                        />
                        {winery.has_deal && (
                          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gold-500 text-white text-xs sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-1">
                            <Tag size={10} weight="fill" className="sm:w-3 sm:h-3" />
                            Deal
                          </span>
                        )}
                      </div>
                      <div className="p-4 sm:p-5">
                        <h3 className="font-serif text-base sm:text-lg text-primary mb-1.5 sm:mb-2">{winery.name}</h3>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-sm text-muted-foreground mb-1.5 sm:mb-2">
                          <MapPin size={12} className="sm:w-[14px] sm:h-[14px]" />
                          {winery.city}, {winery.region}
                        </div>
                        {winery.rating && (
                          <div className="flex items-center gap-1 text-xs sm:text-sm mb-2 sm:mb-3">
                            <Star size={12} weight="fill" className="text-gold-500 sm:w-[14px] sm:h-[14px]" />
                            <span className="font-medium">{winery.rating}</span>
                            <span className="text-muted-foreground">({winery.review_count} reviews)</span>
                          </div>
                        )}
                        {winery.description && (
                          <p className="text-muted-foreground text-sm line-clamp-2">
                            {winery.description}
                          </p>
                        )}
                        
                        {/* Special Deal Section */}
                        {winery.has_deal && (
                          <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-[#f8f5f2] rounded-lg">
                            <p className="text-[#722F37] font-serif font-semibold text-sm sm:text-base mb-1">Special Wine Tasting Deal</p>
                            <p className="text-gray-500 text-[0.6875rem] sm:text-xs mb-3">Available via the Winery Finder App</p>
                            <div className="flex gap-2">
                              <a 
                                href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 bg-black text-white text-xs sm:text-xs px-3 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                                <span className="font-medium">App Store</span>
                              </a>
                              <a 
                                href="https://play.google.com/store/apps/details?id=com.shoutem.n75202&hl=en" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 bg-black text-white text-xs sm:text-xs px-3 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M3.18 2.81L14.24 12 3.18 21.19c-.2-.23-.32-.53-.32-.85V3.66c0-.32.12-.62.32-.85zm1.04-1.08L15.63 12 4.22 22.27c-.17.06-.36.09-.56.09-.23 0-.45-.06-.65-.16l11.48-9.91L3.01 2.38c.2-.1.42-.16.65-.16.2 0 .39.03.56.09v-.58zm15.96 9.19L17.68 12l2.5 1.08c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.12l-2.5 1.08 2.5 1.08c.45.19.75.62.75 1.12s-.3.93-.75 1.12L5.34 24c-.2.09-.42.13-.65.13-.5 0-.97-.2-1.3-.54L14.87 12 3.39.41C3.72.14 4.19-.06 4.69-.06c.23 0 .45.04.65.13l14.84 6.24c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.12l-2.5 1.08 2.5 1.08c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.13z"/>
                                </svg>
                                <span className="font-medium">Google Play</span>
                              </a>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border/50 text-xs sm:text-xs text-muted-foreground">
                          {winery.appointment_required ? (
                            <span>Appointment Required</span>
                          ) : (
                            <span className="text-green-600">Walk-ins Welcome</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

    </>
  );
};

export default Wineries;
